import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle2, XCircle, ExternalLink, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PendingHostApplicationsCard({ userEmail }) {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userEmail) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch admin list as fallback (the public /host/application/{app_id} returns one);
        // since user might have multiple, we use a small public-list endpoint scoped to email.
        const r = await fetch(`${API}/host/applications?email=${encodeURIComponent(userEmail)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        });
        if (r.ok && !cancel) {
          const d = await r.json();
          setApps(d.applications || []);
        }
      } catch {} finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [userEmail]);

  if (loading || apps.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="pending-host-applications">
      {apps.map(app => (
        <Card key={app.application_id} className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
              {app.status === "approved" ? <CheckCircle2 className="w-5 h-5 text-green-600" /> :
               app.status === "rejected" ? <XCircle className="w-5 h-5 text-red-500" /> :
               <Clock className="w-5 h-5 text-amber-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-sm">Host application · {app.category_id}</p>
                <Badge className={`text-[10px] capitalize ${
                  app.status === "approved" ? "bg-green-100 text-green-700" :
                  app.status === "rejected" ? "bg-red-100 text-red-700" :
                  "bg-amber-100 text-amber-700"
                }`}>
                  {app.status}
                </Badge>
              </div>
              <p className="text-xs text-gray-600 mt-0.5">
                {app.status === "pending" && "Admin is reviewing your application. You'll get an email when it's approved."}
                {app.status === "approved" && "Your dashboard is live. Click below to start hosting."}
                {app.status === "rejected" && (app.rejected_reason || "Your application wasn't approved.")}
              </p>
              {app.status === "approved" && app.event_id && (
                <Button
                  size="sm"
                  className="mt-2 bg-purple-600 hover:bg-purple-700"
                  onClick={() => navigate(`/host/v2/${app.event_id}`)}
                  data-testid={`open-dashboard-${app.application_id}`}
                >
                  <Layers className="w-3 h-3 mr-1.5" /> Open my dashboard
                </Button>
              )}
              {app.status === "pending" && (
                <button
                  onClick={() => navigate(`/host/application/${app.application_id}`)}
                  className="mt-2 text-xs text-purple-700 hover:underline flex items-center gap-1"
                  data-testid={`view-app-${app.application_id}`}
                >
                  View application <ExternalLink className="w-3 h-3" />
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
